import React, { useState, useRef, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Days, days } from '@techminds/react-calendar';
import cx from 'classnames';

import Calendar, { CalendarRef } from '@/components/Common/Calendar';
import { DEFAULT_DATE_FORMAT, MONTH_DAY_DAY_TEXT_FORMAT } from '@pkgtour-workspace/common/dist/constants/date';

import styles from './DateRangeField.module.scss';

interface DateRangeFieldProps {
  startDateFieldName: string;
  endDateFieldName: string;
  format?: string;
  required?: boolean;
  prefixLabel?: string;
  onChangeOpen?: (isOpened: boolean) => void;
  placeholder?: string;
  className?: string;
  nlogAreaCode?: string;
  closeNlogAreaCode?: string;
}

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  startDateFieldName,
  endDateFieldName,
  prefixLabel,
  required = false,
  onChangeOpen,
  placeholder = '',
  className,
  nlogAreaCode,
  closeNlogAreaCode = nlogAreaCode,
}) => {
  const { register, setValue, control } = useFormContext();

  register(startDateFieldName, { required });
  register(endDateFieldName, { required });

  const startDate = useWatch({ name: startDateFieldName, control });
  const endDate = useWatch({ name: endDateFieldName, control });

  const [canSubmit, setCanSubmit] = useState(startDate && endDate);

  const handleDayClick = useCallback(([dates]: Days[][] = []) => {
    setCanSubmit(dates.length === 2);
  }, []);

  const startDateDays = days(startDate);
  const endDateDays = days(endDate);

  const calendarRef = useRef<CalendarRef | null>(null);
  const [isOpened, setIsOpened] = useState(false);

  const handleToggle = () => {
    setIsOpened(!isOpened);
    onChangeOpen?.(!isOpened);
  };

  const handleReset = () => {
    setCanSubmit(false);
    calendarRef.current?.reset();
  };
  const handleSubmit = () => {
    const [startDate, endDate] = calendarRef.current?.submit() || [];

    if (required && (!startDate || !endDate)) {
      return;
    }

    setValue(startDateFieldName, startDate?.format(DEFAULT_DATE_FORMAT) || null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(endDateFieldName, endDate?.format(DEFAULT_DATE_FORMAT) || null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setIsOpened(false);
    onChangeOpen?.(false);
  };

  return (
    <div className={cx(styles.candidate, className)}>
      <span className={styles.head}>
        {!!prefixLabel && <span className={styles.index}>{prefixLabel}</span>}
        {startDate && endDate && <i>여행 기간: {endDateDays.diff(startDateDays, 'day') + 1}일</i>}
      </span>
      <div className={styles.date}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={isOpened && 'true'}
          onClick={handleToggle}
          {...(nlogAreaCode
            ? { 'data-event-nlog': 'click', 'data-event-area-code': isOpened ? nlogAreaCode : closeNlogAreaCode }
            : {})}
        >
          {startDate && endDate
            ? `${startDateDays.format(MONTH_DAY_DAY_TEXT_FORMAT)} ~ ${endDateDays.format(MONTH_DAY_DAY_TEXT_FORMAT)}`
            : placeholder}
        </button>
        {isOpened && (
          <>
            <div className={styles.dayofweek} aria-hidden>
              {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className={styles.calendar}>
              <Calendar
                ref={calendarRef}
                defaultValue={startDate && endDate ? [startDateDays, endDateDays] : undefined}
                onDayClick={handleDayClick}
              />
            </div>
            <div className={styles.control}>
              <button
                type="button"
                className={styles.reset}
                onClick={handleReset}
                data-event-nlog="click"
                data-event-area-code="cus.selfcalenarunchoose"
              >
                선택 해제
              </button>
              <button
                type="button"
                className={styles.complete}
                onClick={handleSubmit}
                data-event-nlog="click"
                data-event-area-code="cus.selfcalendarcomplete"
                disabled={!canSubmit}
              >
                선택 완료
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DateRangeField;
